import React, { useRef, useState, useEffect, useCallback } from 'react';
import './TextEditor.css';
import PDFPageSelectorModal from './PDFPageSelectorModal';
import {
  ShapeToolbar
} from './shapes';

import {
  getPointerCoords,
  shouldIgnoreKeyboardEvent,
  isYouTubeLink,
  getYouTubeId,
} from '../utils/whiteboardUtils';
import { useWhiteboardPan } from '../hooks/useWhiteboardPan';
import { useWhiteboardSticker } from '../hooks/useWhiteboardSticker';
import { useWhiteboardGroupSelect } from '../hooks/useWhiteboardGroupSelect';
import { useWhiteboardEraser } from '../hooks/useWhiteboardEraser';
import { useWhiteboardHotkeys } from '../hooks/useWhiteboardHotkeys';
import { useWhiteboardBoardHandlers } from '../hooks/useWhiteboardBoardHandlers';
import { useWhiteboardPointerHandlers } from '../hooks/useWhiteboardPointerHandlers';
import { useWhiteboardElements } from '../hooks/useWhiteboardElements';
import { useWhiteboardShapeActions } from '../hooks/useWhiteboardShapeActions';
import { useWhiteboardGlobalEffects } from '../hooks/useWhiteboardGlobalEffects';
import WhiteboardToolbar from './WhiteboardToolbar';
import WhiteboardCanvas from './WhiteboardCanvas';
import WhiteboardSelectionOverlay from './WhiteboardSelectionOverlay';
import { migrateMediaSrcToStorage } from '../utils/storageService';

function MiroLikeWhiteboard({ id, initialContent, onContentChange, onInsertText, isStudent = false, isViewOnly = false, remotePatch = null, onBroadcastPatch, onBroadcastViewport, remoteViewport, userId, lessonId }) {
  // Students always get full tool access regardless of lesson view_only mode
  const effectiveViewOnly = isStudent ? false : isViewOnly;
  const textSizeTimeoutRef = useRef({});
  
  // Глобальная обработка ошибок DOM
  useEffect(() => {
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function(child) {
      try {
        if (this.contains(child)) {
          return originalRemoveChild.call(this, child);
        }
      } catch (error) {

      }
    };

    const timeoutsRef = textSizeTimeoutRef;
    return () => {
      Node.prototype.removeChild = originalRemoveChild;
      
      const timeouts = timeoutsRef.current;
      Object.values(timeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  // Убираем стрелки/линии с NaN в x1,y1,x2,y2 — иначе циклы и ошибки в DOM (вызов только в useEffect/при setShapes)
  const sanitizeShapes = useCallback((arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.filter(shape => {
      if (!shape || typeof shape !== 'object' || !('id' in shape)) return false;
      if (shape.type === 'arrow' || shape.type === 'line') {
        const { x1, y1, x2, y2 } = shape;
        return Number.isFinite(x1) && Number.isFinite(y1) && Number.isFinite(x2) && Number.isFinite(y2);
      }
      return true;
    });
  }, []);

  const [shapes, setShapes] = useState(() => {
    const filterValid = (list) => (list || []).filter(shape => shape && typeof shape === 'object' && 'id' in shape);
    const dropBadSegment = (list) => list.filter(shape => {
      if (shape.type === 'arrow' || shape.type === 'line') {
        const { x1, y1, x2, y2 } = shape;
        return Number.isFinite(x1) && Number.isFinite(y1) && Number.isFinite(x2) && Number.isFinite(y2);
      }
      return true;
    });
    if (initialContent && initialContent.shapes) {
      return dropBadSegment(filterValid(initialContent.shapes));
    }
    return [];
  });

  // Apply remote realtime patches (teacher ↔ student bidirectional)
  useEffect(() => {
    if (!remotePatch) return;
    if (remotePatch.type === 'expand' && remotePatch.shapeId) {
      if (Array.isArray(remotePatch.shapes)) {
        const sanitized = sanitizeShapes(remotePatch.shapes);
        lastRemoteShapesRef.current = sanitized;
        setShapes(sanitized);
      }
      const expandedWidth = remotePatch.width || 900;
      const expandedHeight = remotePatch.height || 700;
      setShapes(prev => {
        const next = prev.map(shape =>
          shape.id === remotePatch.shapeId
            ? { ...shape, width: expandedWidth, height: expandedHeight }
            : shape
        );
        lastRemoteShapesRef.current = next;
        return next;
      });
      if (typeof remotePatch.pan === 'object' && panRef && panRef.current) {
        const next = {
          x: typeof remotePatch.pan.x === 'number' ? remotePatch.pan.x : panRef.current.x,
          y: typeof remotePatch.pan.y === 'number' ? remotePatch.pan.y : panRef.current.y,
          scale: typeof remotePatch.pan.scale === 'number' ? remotePatch.pan.scale : panRef.current.scale,
        };
        updatePan(next);
      }
    } else if (remotePatch.type === 'shapes' && Array.isArray(remotePatch.shapes)) {
      const sanitized = sanitizeShapes(remotePatch.shapes);
      lastRemoteShapesRef.current = sanitized;
      setShapes(sanitized);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remotePatch, sanitizeShapes]);

  // Один раз при монтировании убираем из state стрелки/линии с NaN (если попали из-за бага)
  useEffect(() => {
    setShapes(prev => sanitizeShapes(prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastMousePosition = useRef({ x: 0, y: 0 });
  // Rubber-band selection box — driven by direct DOM mutation to avoid re-renders
  const selectionRectRef = useRef(null);
  const selectionCoordRef = useRef(null);

  // Notify parent of shapes changes (debounced — avoids firing on every drag frame)
  const onContentChangeRef = useRef(onContentChange);
  onContentChangeRef.current = onContentChange;
  const debounceTimerRef = useRef(null);
  // Tracks the array reference last written by a remote patch so we can skip
  // broadcasting it back (prevents the remote→setShapes→onContentChange→broadcast→remote loop)
  const lastRemoteShapesRef = useRef(null);

  const latestShapesRef = useRef(shapes);
  latestShapesRef.current = shapes;

  useEffect(() => {
    // If this shapes value was applied from an incoming patch, don't echo it back
    if (shapes === lastRemoteShapesRef.current) return;
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      if (onContentChangeRef.current) {
        onContentChangeRef.current({ shapes });
      }
    }, 300);
    return () => clearTimeout(debounceTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapes]);

  // Flush any still-pending debounced save on unmount instead of letting the
  // cleanup above silently cancel it. The Board tab fully unmounts this
  // component whenever the user switches to another tab (see
  // LessonWorkspace.js renderContent()), so a change made right before
  // switching — e.g. expanding/collapsing an exercise — could otherwise be
  // dropped and appear to "revert" the next time the Board tab is shown.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        if (onContentChangeRef.current) {
          onContentChangeRef.current({ shapes: latestShapesRef.current });
        }
      }
    };
  }, []);

  // Undo/redo history
  const historyRef = useRef([]);   // past snapshots (oldest → newest)
  const futureRef = useRef([]);    // redo snapshots (next → furthest)
  const isUndoRedoRef = useRef(false);
  const prevShapesRef = useRef(shapes);
  const historyTimerRef = useRef(null);

  useEffect(() => {
    if (isUndoRedoRef.current) {
      isUndoRedoRef.current = false;
      prevShapesRef.current = shapes;
      return;
    }
    // Debounce: rapid changes (drag frames) collapse into one history entry
    const snapshot = prevShapesRef.current;
    clearTimeout(historyTimerRef.current);
    historyTimerRef.current = setTimeout(() => {
      historyRef.current = [...historyRef.current, snapshot].slice(-50);
      futureRef.current = [];
      prevShapesRef.current = shapes;
    }, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapes]);

  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return;
    const prev = historyRef.current[historyRef.current.length - 1];
    historyRef.current = historyRef.current.slice(0, -1);
    futureRef.current = [shapes, ...futureRef.current].slice(0, 50);
    isUndoRedoRef.current = true;
    clearTimeout(historyTimerRef.current);
    setShapes(prev);
  }, [shapes]);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return;
    const next = futureRef.current[0];
    futureRef.current = futureRef.current.slice(1);
    historyRef.current = [...historyRef.current, shapes].slice(-50);
    isUndoRedoRef.current = true;
    clearTimeout(historyTimerRef.current);
    setShapes(next);
  }, [shapes]);

  // pan/zoom state
  const [tool, setTool] = useState('select');
  const [penType, setPenType] = useState('pen'); // pen, highlighter, eraser
  const [penColor, setPenColor] = useState('#222');
  const [penThickness, setPenThickness] = useState(3);

  // Shape drawing states
  const [isDrawingShape, setIsDrawingShape] = useState(false);
  const [drawingShape, setDrawingShape] = useState(null);
  const [shapeStartPoint, setShapeStartPoint] = useState(null);

  const [dragged, setDragged] = useState(null); // {id, offsetX, offsetY}
  const svgRef = useRef();
  const isDrawing = useRef(false);
  const gridCanvasRef = useRef();

  const [currentLine, setCurrentLine] = useState(null); // {points, color, thickness, alpha, type}
  // --- Стили для текста ---

  // Состояния для текстового инструмента

  const [textSizes, setTextSizes] = useState({}); // Состояние для отслеживания размеров текстовых блоков

  const [selectedShape, setSelectedShape] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState(null);
  const [originalSize, setOriginalSize] = useState(null);
  const [startShapes, setStartShapes] = useState(null);
  const eraserPrevRef = useRef(null);
  const clipboardRef = useRef([]);
  const [resizing, setResizing] = useState(null);

  // Shape toolbar popup state
  const [shapeToolbar, setShapeToolbar] = useState(null); // { shape, x, y }



  // PDF Page Selector Modal state
  const [pdfModal, setPdfModal] = useState(null); // { pdfUrl, x, y }

  // Drag overlay state
  const [isDragOver, setIsDragOver] = useState(false);

  const {
    pan, panRef, isPanning, lastPan,
    updatePan, spacePressed, setSpacePressed,
    isDragging,
    dragPanStart, dragPanMove, dragPanEnd,
    handleZoomIn, handleZoomOut, handleZoomToFit, zoomPercent,
  } = useWhiteboardPan(id, svgRef, tool, { setIsDragOver, setShapes, setPdfModal });

  // Teacher: broadcast pan/zoom in Follow Mode so student mirrors the viewport
  useEffect(() => {
    if (isStudent || !onBroadcastViewport) return;
    onBroadcastViewport({ x: pan.x, y: pan.y, scale: pan.scale });
  }, [pan]); // eslint-disable-line react-hooks/exhaustive-deps

  // Student: apply teacher viewport when in Follow Mode
  useEffect(() => {
    if (!remoteViewport) return;
    if (typeof remoteViewport.x !== 'number') return;
    updatePan({ x: remoteViewport.x, y: remoteViewport.y, scale: remoteViewport.scale });
  }, [remoteViewport]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    stickerPopup, setStickerPopup,
    editingStickerId, setEditingStickerId,
    editingTextId, setEditingTextId,
    stickerEditRef,
    draggedSticker,
    handleStickerClick,
  } = useWhiteboardSticker(setTool, setSelectedShape, setShapes, panRef, svgRef, tool);

  const selectedTextShape = selectedShape
    ? (shapes.find(s => s.id === selectedShape && s.type === 'text') || null)
    : null;

  const handleTextShapeUpdate = useCallback((updates) => {
    setShapes(prev => prev.map(s => s.id === selectedShape ? { ...s, ...updates } : s));
  }, [selectedShape]);

  // Always-fresh refs so the window global never holds stale shape/fn data
  const selectedTextShapeRef = useRef(null);
  selectedTextShapeRef.current = selectedTextShape;
  const handleTextShapeUpdateRef = useRef(handleTextShapeUpdate);
  handleTextShapeUpdateRef.current = handleTextShapeUpdate;

  // Expose selected whiteboard text shape to LessonToolbar via window global
  useEffect(() => {
    if (selectedTextShape) {
      window.selectedWhiteboardTextShape = {
        get shape() { return selectedTextShapeRef.current; },
        get updateFn() { return handleTextShapeUpdateRef.current; },
      };
      if (window.updateToolbar) window.updateToolbar();
    } else {
      window.selectedWhiteboardTextShape = null;
      if (window.updateToolbar) window.updateToolbar();
    }
    return () => { window.selectedWhiteboardTextShape = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTextShape, handleTextShapeUpdate]);

  const createTextBlock = (x, y, text = '') => {
    const newTextShape = {
      id: Date.now() + Math.random(),
      type: 'text',
      x: x,
      y: y,
      width: 300,
      height: 80,
      text: text,
      fontSize: 24,
      color: '#222',
      font: 'Arial, sans-serif',
      bold: false
    };

    setShapes(prev => [...prev, newTextShape]);
    setSelectedShape(newTextShape.id);
    setEditingTextId(newTextShape.id);
  };


  const {
    selectedShapes, setSelectedShapes,
    selectionStartRef, isBoxSelectingRef,
    isGroupDragging, setIsGroupDragging, groupDragStart,
    isGroupResizing,
    handleGroupMouseDown, handleGroupMouseMove, handleGroupMouseUp,
    handleGroupResizeMouseDown, handleGroupResizeMouseMove, handleGroupResizeMouseUp,
  } = useWhiteboardGroupSelect(shapes, setShapes, selectedShape, setSelectedShape, setDragged, pan, svgRef, tool, shouldIgnoreKeyboardEvent);

  const {
    eraserSize,
    eraserTrail, setEraserTrail,
    isErasing, setIsErasing,
    eraserShadow, setEraserShadow,
    fadedShapes, setFadedShapes,
    handleEraserClick,
  } = useWhiteboardEraser(setTool, setPenType);

  useWhiteboardHotkeys({
    setTool, setPenType, setSpacePressed, updatePan, panRef, undo, redo,
    shapes, selectedShape, selectedShapes, setShapes, setSelectedShape, setSelectedShapes, clipboardRef,
    createTextBlock, lastMousePosition,
  });

  const { handleBoardMouseDown, handleBoardMouseMove, handleBoardMouseUp } = useWhiteboardBoardHandlers({
    isStudent,
    svgRef, panRef, pan,
    shapes, setShapes,
    selectedShape, setSelectedShape,
    selectedShapes, setSelectedShapes,
    selectionStartRef, isBoxSelectingRef,
    selectionRectRef, selectionCoordRef,
    isGroupDragging, setIsGroupDragging, groupDragStart,
    setDragged,
    tool, setTool,
    shapeToolbar, setShapeToolbar,
    lastMousePosition,
    spacePressed, isPanning, lastPan, updatePan,
    isDragging, resizing,
  });

  const { handlePointerDown, handlePointerMove, handlePointerUp } = useWhiteboardPointerHandlers({
    svgRef, panRef,
    shapes, setShapes,
    selectedShape, setSelectedShape,
    selectedShapes, setSelectedShapes,
    selectionStartRef,
    isGroupDragging,
    dragged, setDragged,
    tool, setTool,
    penType, penColor, penThickness,
    isDrawingShape, setIsDrawingShape,
    drawingShape, setDrawingShape,
    shapeStartPoint, setShapeStartPoint,
    isDrawing,
    currentLine, setCurrentLine,
    eraserSize, isErasing, setIsErasing,
    eraserTrail, setEraserTrail,
    eraserShadow, setEraserShadow,
    fadedShapes, setFadedShapes,
    eraserPrevRef,
    isResizing, setIsResizing,
    resizeStart, setResizeStart,
    originalSize, setOriginalSize,
    startShapes, setStartShapes,
    resizing, setResizing,
    setEditingTextId,
    stickerPopup, setStickerPopup,
    createTextBlock,
    updatePan,
  });

  // Toast state
  const [toast] = useState(null);




  const {
    centerOnExercise,
  } = useWhiteboardElements({
    setShapes, setPdfModal,
    panRef, updatePan, shapes,
    onInsertText, onBroadcastPatch,
  });

  const {
    handlePdfModalClose,
    handleShapeChange, handleShapeDuplicate, handleShapeDelete,
    handleShapeBringForward, handleShapeSendBackward,
    handlePdfPagesInsert,
  } = useWhiteboardShapeActions({ setShapes, setSelectedShape, setPdfModal });

  useWhiteboardGlobalEffects({
    gridCanvasRef,
    resizing, setResizing,
    setShapes,
    selectedShape, setSelectedShape,
    setDragged, setEditingStickerId,
    shouldIgnoreKeyboardEvent,
    createTextBlock, lastMousePosition,
  });

  // Prevent iOS Safari scroll/bounce while whiteboard is mounted
  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('touchmove', prevent, { passive: false });
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.removeEventListener('touchmove', prevent);
    };
  }, []);

  // --- YouTube click handler ---
  const handleYouTubeClick = (shapeId) => {
    setShapes(prev => prev.map(s =>
      s.id === shapeId && s.type === 'youtube' ? { ...s, isPlaying: true } : s
    ));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    // Handle dropped URL (e.g. YouTube link dragged from browser address bar)
    const droppedUrl = (e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain') || '').trim();
    if (droppedUrl && isYouTubeLink(droppedUrl)) {
      const videoId = getYouTubeId(droppedUrl);
      if (videoId) {
        const { x, y } = getPointerCoords(e, panRef.current, svgRef);
        setShapes(prev => [...prev, {
          id: Date.now(), type: 'youtube', x, y,
          width: 560, height: 315, videoId, isPlaying: false, clickCount: 0,
        }]);
        return;
      }
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      const blobUrl = URL.createObjectURL(file);
      const { x, y } = getPointerCoords(e, panRef.current, svgRef);

      if (file.type.startsWith('image/')) {
        const shapeId = Date.now();
        // Insert immediately with blob URL so teacher sees it right away
        setShapes(prev => [...prev, {
          id: shapeId,
          type: 'image',
          x,
          y,
          width: 200,
          height: 150,
          src: blobUrl,
          filename: file.name
        }]);
        // Upload to Storage in the background and replace blob URL with permanent URL
        if (userId && lessonId) {
          migrateMediaSrcToStorage(blobUrl, userId, lessonId).then(permanentUrl => {
            if (permanentUrl && permanentUrl !== blobUrl) {
              setShapes(prev => prev.map(s => s.id === shapeId ? { ...s, src: permanentUrl } : s));
              URL.revokeObjectURL(blobUrl);
            }
          }).catch(() => {});
        }
      } else if (file.type.startsWith('video/')) {
        const shapeId = Date.now();
        setShapes(prev => [...prev, {
          id: shapeId, type: 'video', x, y, width: 480, height: 270, src: blobUrl, filename: file.name, isPlaying: false,
        }]);
        if (userId && lessonId) {
          migrateMediaSrcToStorage(blobUrl, userId, lessonId).then(permanentUrl => {
            if (permanentUrl && permanentUrl !== blobUrl) {
              setShapes(prev => prev.map(s => s.id === shapeId ? { ...s, src: permanentUrl } : s));
              URL.revokeObjectURL(blobUrl);
            }
          }).catch(() => {});
        }
      } else if (file.type.startsWith('audio/')) {
        const shapeId = Date.now();
        setShapes(prev => [...prev, {
          id: shapeId, type: 'audio', x, y, width: 300, height: 80, src: blobUrl, filename: file.name, isPlaying: false,
        }]);
        if (userId && lessonId) {
          migrateMediaSrcToStorage(blobUrl, userId, lessonId).then(permanentUrl => {
            if (permanentUrl && permanentUrl !== blobUrl) {
              setShapes(prev => prev.map(s => s.id === shapeId ? { ...s, src: permanentUrl } : s));
              URL.revokeObjectURL(blobUrl);
            }
          }).catch(() => {});
        }
      } else if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        // Handle PDF files
        setPdfModal({ pdfUrl: blobUrl, filename: file.name, x, y });
      }
    }
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Add drag enter/leave handlers for visual feedback
  const handleDragEnter = e => {
    e.preventDefault();
    document.body.style.cursor = 'copy';
  };

  const handleDragLeave = e => {
    e.preventDefault();
    document.body.style.cursor = 'default';
  };






  // --- Overlay input и тулбар через Portal ---


  // Обработчик клавиатуры для создания текста при вводе


  



























  // Сбрасываем флаг box selection при снятии выделения
  useEffect(() => {
    if (selectedShapes.length === 0) {
      // Также сбрасываем флаг box selection
      isBoxSelectingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShapes.length]);

  // Сбрасываем флаг box selection при снятии выделения
  useEffect(() => {
    if (!selectedShape && selectedShapes.length === 0) {
      isBoxSelectingRef.current = false;
  
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShape, selectedShapes.length]);

  return (
    <div
      className="whiteboard-container"
      data-whiteboard-id={id}
      data-tool={tool}
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 100,
        background: '#fff',
        overflow: 'hidden',
        outline: 'none',
        touchAction: 'none',
      }}
      tabIndex={-1}
      onMouseDown={handleBoardMouseDown}
      onMouseMove={handleBoardMouseMove}
      onMouseUp={handleBoardMouseUp}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
    >
      {/* Toast */}
      {toast && (
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: '#4CAF50',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10001,
            fontSize: '14px'
          }}
        >
          {toast}
        </div>
      )}

      {/* Grid Dots Canvas */}
      <canvas
        ref={gridCanvasRef}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
                        userSelect: 'none',
                        touchAction: 'none',
                        zIndex: -1,
        }}
      />
      <WhiteboardToolbar
        tool={tool}
        setTool={setTool}
        setPenType={setPenType}
        penColor={penColor}
        setPenColor={setPenColor}
        penThickness={penThickness}
        setPenThickness={setPenThickness}
        stickerPopup={stickerPopup}
        setStickerPopup={setStickerPopup}
        pan={pan}
        setShapes={setShapes}
        isViewOnly={effectiveViewOnly}
        isStudent={isStudent}
        handleStickerClick={handleStickerClick}
        handleEraserClick={handleEraserClick}
      />
      {/* Canvas + SVG */}
      <WhiteboardCanvas
        svgRef={svgRef}
        panRef={panRef}
        pan={pan}
        shapes={shapes}
        setShapes={setShapes}
        selectedShape={selectedShape}
        setSelectedShape={setSelectedShape}
        tool={tool}
        isViewOnly={effectiveViewOnly}
        isStudent={isStudent}
        drawingShape={drawingShape}
        currentLine={currentLine}
        fadedShapes={fadedShapes}
        eraserShadow={eraserShadow}
        isErasing={isErasing}
        eraserSize={eraserSize}
        shapeToolbar={shapeToolbar}
        setShapeToolbar={setShapeToolbar}
        editingStickerId={editingStickerId}
        setEditingStickerId={setEditingStickerId}
        editingTextId={editingTextId}
        setEditingTextId={setEditingTextId}
        textSizes={textSizes}
        setTextSizes={setTextSizes}
        setDragged={setDragged}
        draggedSticker={draggedSticker}
        setIsResizing={setIsResizing}
        setOriginalSize={setOriginalSize}
        setResizeStart={setResizeStart}
        setResizing={setResizing}
        setStartShapes={setStartShapes}
        stickerEditRef={stickerEditRef}
        centerOnExercise={centerOnExercise}
        dragPanStart={dragPanStart}
        dragPanMove={dragPanMove}
        dragPanEnd={dragPanEnd}
        handlePointerDown={handlePointerDown}
        handlePointerMove={handlePointerMove}
        handlePointerUp={handlePointerUp}
        handleBoardMouseDown={handleBoardMouseDown}
        handleYouTubeClick={handleYouTubeClick}
      />
      {/* Zoom Control */}
      {!effectiveViewOnly && (
        <div style={{
          position: 'fixed',
          left: 20,
          bottom: 20,
          zIndex: 1000,
          background: '#fff',
          borderRadius: 8,
          boxShadow: '0 2px 8px #bbb2',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 10px',
          fontSize: 15,
          userSelect: 'none',
        }}>
          <button onClick={handleZoomOut} title="Zoom out" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, fontSize: 20, fontWeight: 600, width: 28, height: 28, borderRadius: 6, color: '#000' }}>
            –
          </button>
          <span 
            onClick={handleZoomToFit} 
            title="Fit to screen (10%)" 
            style={{ 
              width: 38, 
              textAlign: 'center', 
              fontVariantNumeric: 'tabular-nums', 
              fontWeight: 500,
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            {zoomPercent}%
          </span>
          <button onClick={handleZoomIn} title="Zoom in" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, fontSize: 20, fontWeight: 600, width: 28, height: 28, borderRadius: 6, color: '#000' }}>
            +
          </button>
        </div>
      )}
      {/* Selection + Group frames */}
      <WhiteboardSelectionOverlay
        selectionRectRef={selectionRectRef}
        pan={pan}
        isViewOnly={effectiveViewOnly}
        shapes={shapes}
        selectedShapes={selectedShapes}
        isGroupDragging={isGroupDragging}
        isGroupResizing={isGroupResizing}
        handleGroupMouseDown={handleGroupMouseDown}
        handleGroupMouseMove={handleGroupMouseMove}
        handleGroupMouseUp={handleGroupMouseUp}
        handleGroupResizeMouseDown={handleGroupResizeMouseDown}
        handleGroupResizeMouseMove={handleGroupResizeMouseMove}
        handleGroupResizeMouseUp={handleGroupResizeMouseUp}
      />
      {/* PDF Page Selector Modal */}
      {pdfModal && (
        <PDFPageSelectorModal
          pdfUrl={pdfModal.pdfUrl}
          x={pdfModal.x}
          y={pdfModal.y}
          onClose={handlePdfModalClose}
          onInsertPages={handlePdfPagesInsert}
        />
      )}
      
      {/* Drag Overlay */}
      {isDragOver && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.6) 0%, rgba(118, 75, 162, 0.6) 100%)',
            backgroundSize: '400% 400%',
            animation: 'flow 3s ease-in-out infinite',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            pointerEvents: 'none',
            color: 'white',
            fontSize: '25px',
            fontWeight: 'bold',
            textAlign: 'center'
          }}
        >
          Drop it like it's hot 🔥
          <style>{`
            @keyframes flow {
              0% {
                background-position: 0% 50%;
              }
              50% {
                background-position: 100% 50%;
              }
              100% {
                background-position: 0% 50%;
              }
            }
          `}</style>
        </div>
      )}
      


      {/* Shape Toolbar */}
      {!effectiveViewOnly && shapeToolbar && (
        <ShapeToolbar
          shape={shapeToolbar.shape}
          onShapeChange={handleShapeChange}
          onDuplicate={handleShapeDuplicate}
          onDelete={handleShapeDelete}
          onBringForward={handleShapeBringForward}
          onSendBackward={handleShapeSendBackward}
          x={shapeToolbar.x}
          y={shapeToolbar.y}
          onClose={() => setShapeToolbar(null)}
        />
      )}
    </div>
  );
};

export default MiroLikeWhiteboard;
